import { OUTLINE_MIN_CORNERS } from './roomOutline';
import { Point } from './mapGeometry';

export type DrawTool = 'polyline' | 'rectangle' | 'freehand' | 'edit';

export interface OutlineSnapshot {
    corners: Point[];
    closed: boolean;
}

export type DrawSelection = { kind: 'vertex' | 'segment'; index: number } | null;

export interface DrawSession {
    outline: OutlineSnapshot;
    past: OutlineSnapshot[];
    future: OutlineSnapshot[];
    tool: DrawTool;
    selection: DrawSelection;
    snapToGrid: boolean;
    snapToAngle: boolean;
}

export type DrawAction =
    | { type: 'START'; payload: OutlineSnapshot }
    | { type: 'SET_TOOL'; payload: DrawTool }
    | { type: 'SET_SNAPPING'; payload: { snapToGrid?: boolean; snapToAngle?: boolean } }
    | { type: 'SELECT'; payload: DrawSelection }
    | { type: 'ADD_CORNER'; payload: Point }
    | { type: 'CLOSE' }
    | { type: 'REPLACE_OUTLINE'; payload: OutlineSnapshot }
    | { type: 'MOVE_CORNER'; payload: { index: number; point: Point } }
    | { type: 'DELETE_SELECTION' }
    | { type: 'UNDO' }
    | { type: 'REDO' };

export const INITIAL_DRAW_SESSION: DrawSession = {
    outline: { corners: [], closed: false },
    past: [],
    future: [],
    tool: 'polyline',
    selection: null,
    snapToGrid: true,
    snapToAngle: true,
};

function settleTool(closed: boolean, tool: DrawTool): DrawTool {
    if (closed) return 'edit';
    return tool === 'edit' ? 'polyline' : tool;
}

function commit(state: DrawSession, outline: OutlineSnapshot): DrawSession {
    return {
        ...state,
        outline,
        past: [...state.past, state.outline],
        future: [],
        selection: null,
        tool: settleTool(outline.closed, state.tool),
    };
}

function deleteSelection(
    outline: OutlineSnapshot,
    selection: DrawSelection
): OutlineSnapshot | null {
    if (!selection) return null;
    const { corners, closed } = outline;
    if (selection.kind === 'vertex') {
        const remaining = corners.filter((_, i) => i !== selection.index);
        return { corners: remaining, closed: closed && remaining.length >= OUTLINE_MIN_CORNERS };
    }
    if (!closed) return null;
    // Removing edge i→i+1 opens the ring into the chain i+1 … i, so drawing resumes from corner i.
    const start = selection.index + 1;
    return { corners: [...corners.slice(start), ...corners.slice(0, start)], closed: false };
}

export function drawSessionReducer(state: DrawSession, action: DrawAction): DrawSession {
    const { corners, closed } = state.outline;
    switch (action.type) {
        case 'START':
            return {
                ...state,
                outline: action.payload,
                past: [],
                future: [],
                selection: null,
                tool: settleTool(action.payload.closed, 'polyline'),
            };
        case 'SET_TOOL':
            return { ...state, tool: action.payload, selection: null };
        case 'SET_SNAPPING':
            return { ...state, ...action.payload };
        case 'SELECT':
            return { ...state, selection: action.payload };
        case 'ADD_CORNER': {
            const last = corners[corners.length - 1];
            const isRepeat = !!last && last.x === action.payload.x && last.y === action.payload.y;
            if (closed || isRepeat) return state;
            return commit(state, { corners: [...corners, action.payload], closed: false });
        }
        case 'CLOSE':
            if (closed || corners.length < OUTLINE_MIN_CORNERS) return state;
            return commit(state, { corners, closed: true });
        case 'REPLACE_OUTLINE':
            return commit(state, action.payload);
        case 'MOVE_CORNER': {
            const { index, point } = action.payload;
            const current = corners[index];
            if (!current || (current.x === point.x && current.y === point.y)) return state;
            const moved = corners.map((c, i) => (i === index ? point : c));
            return {
                ...commit(state, { corners: moved, closed }),
                selection: state.selection,
                tool: state.tool,
            };
        }
        case 'DELETE_SELECTION': {
            const next = deleteSelection(state.outline, state.selection);
            return next ? commit(state, next) : state;
        }
        case 'UNDO': {
            const previous = state.past[state.past.length - 1];
            if (!previous) return state;
            return {
                ...state,
                outline: previous,
                past: state.past.slice(0, -1),
                future: [state.outline, ...state.future],
                selection: null,
                tool: settleTool(previous.closed, state.tool),
            };
        }
        case 'REDO': {
            const [next, ...rest] = state.future;
            if (!next) return state;
            return {
                ...state,
                outline: next,
                past: [...state.past, state.outline],
                future: rest,
                selection: null,
                tool: settleTool(next.closed, state.tool),
            };
        }
        default:
            return state;
    }
}
